
import React, { useState, useEffect, useRef } from 'react';
import { getAcctNoFromUrl } from '../../utils/appointments/accountContext';
// Headless UI Combobox and Heroicons imports for category dropdown
import { Combobox, ComboboxOption } from '../../fieldsComponents/appointments/combobox';

import { CogIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import AppointmentSetupPage from './appointmentSetupPage';
import { Button } from '../../fieldsComponents/appointments/button';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Switch, SwitchField } from '../../fieldsComponents/appointments/switch';
import { Label } from '../../fieldsComponents/appointments/fieldset';

import { useNotifications } from '../../components/appointments/notifications';
import DeleteConfirmation from '../../components/appointments/deleteConfirmation';
import { FaPencilAlt, FaUserCircle, FaRegCopy, FaCamera } from 'react-icons/fa'; // Added FaCamera
import AddCategoryDialog from '../../components/hotelBooking/addBookingCalendarDialog'; // Import the AddCalendarDialog component
import { useLocation } from 'react-router-dom';
import { cleanupAccounts } from '../../utils/appointments/urlUtils';
import debounce from 'lodash.debounce'; // Import lodash debounce function

import { PlusIcon, TrashIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useOutletContext } from "react-router-dom";
import LoadingMask from '../../components/appointments/loadingMask';


function GoogleCalendarPanel({ acc }) {
  // Currency state for price input
  const [currency, setCurrency] = useState('INR');

  // Spinner state for hotel image compression
  const [isCompressingHotelImages, setIsCompressingHotelImages] = useState(false);
  const [isAppointmentSetupOpen, setIsAppointmentSetupOpen] = useState(false);
  // State for category ID input field below Connect/Disconnect button

  // --- Hotel booking fields state and handlers ---
  // Days of week
  const daysOfWeek = [
    { label: 'Mon', value: 'Mon' },
    { label: 'Tue', value: 'Tue' },
    { label: 'Wed', value: 'Wed' },
    { label: 'Thu', value: 'Thu' },
    { label: 'Fri', value: 'Fri' },
    { label: 'Sat', value: 'Sat' },
    { label: 'Sun', value: 'Sun' },
  ];
  const [selectedDays, setSelectedDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']); // all days
  const handleDayChange = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
    setIsSaveEnabled(true);
    setIsFormChanged(true);
  };
  const [numRooms, setNumRooms] = useState(3); // 3 rooms
  const [price, setPrice] = useState(''); // empty
  const [extraPersonCharge, setExtraPersonCharge] = useState(''); // empty
  const [additionalInfo, setAdditionalInfo] = useState(''); // empty
  const [checkIn, setCheckIn] = useState('2:00 PM'); // 2:00 PM
  const [checkOut, setCheckOut] = useState('11:00 AM'); // 11:00 AM
  const [maxPersons, setMaxPersons] = useState(4); // 4 adults
  const availableDatesOptions = [
    { id: 'future', name: 'Any date in future' },
    { id: 'range', name: 'Date Range' },
    { id: 'rolling', name: 'Rolling Days' },
  ];
  const [availableDatesOption, setAvailableDatesOption] = useState('future');
  const [rollingDays, setRollingDays] = useState(''); // 60 days
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  // Unavailable Dates
  const [unavailableDates, setUnavailableDates] = useState([]);
  // Toggle for unavailable dates selection mode (range or multi)
  const [unavailableDatesRangeMode, setUnavailableDatesRangeMode] = useState(false);
  // Hotel Pictures
  const [hotelPictures, setHotelPictures] = useState([]);
  // Track which hotel images are still being compressed
  const [compressingHotelImageIdxs, setCompressingHotelImageIdxs] = useState([]);
  // Time options for check in/out (1-12 hours, AM/PM separate)
  const hourOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const ampmOptions = ['AM', 'PM'];

  // Helper to split and join time
  function splitTime12h(time) {
    if (!time) return { hour: '', ampm: 'AM' };
    const match = time.match(/^(\d{1,2}):?\d{0,2}\s*(AM|PM)$/i);
    if (match) return { hour: match[1], ampm: match[2].toUpperCase() };
    // fallback: try to parse 24h
    let [h, m] = time.split(':');
    h = parseInt(h, 10);
    let ampm = h >= 12 ? 'PM' : 'AM';
    let hour = ((h % 12) || 12).toString();
    return { hour, ampm };
  }
  function joinTime12h(hour, ampm) {
    return hour ? `${hour}:00 ${ampm}` : '';
  }

  // State for checkIn/checkOut hour/ampm
  const [checkInHour, setCheckInHour] = React.useState('2');
  const [checkInAMPM, setCheckInAMPM] = React.useState('PM');
  const [checkOutHour, setCheckOutHour] = React.useState('11');
  const [checkOutAMPM, setCheckOutAMPM] = React.useState('AM');

  // Sync checkIn/checkOut when hour/ampm changes
  React.useEffect(() => {
    setCheckIn(joinTime12h(checkInHour, checkInAMPM));
    setIsSaveEnabled(true);
    setIsFormChanged(true);
    // eslint-disable-next-line
  }, [checkInHour, checkInAMPM]);
  React.useEffect(() => {
    setCheckOut(joinTime12h(checkOutHour, checkOutAMPM));
    setIsSaveEnabled(true);
    setIsFormChanged(true);
    // eslint-disable-next-line
  }, [checkOutHour, checkOutAMPM]);
  // Hotel picture handlers

  // Only store selected files in state, do not upload here
  const handleHotelPictureChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return; // Check if files are selected
    if (hotelPictures.length + files.length > 3) {
      showError('You can only upload up to 3 hotel images.');
      e.target.value = '';
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png'];
    const startIdx = hotelPictures.length;
    let newPics = [];
    let newCompressingIdxs = [];
    for (let idx = 0; idx < Math.min(files.length, 3 - hotelPictures.length); idx++) {
      let file = files[idx];
      if (!allowedTypes.includes(file.type)) {
        showError('Only JPEG and PNG images are allowed.');
        continue;
      }
      // Show preview immediately, mark as compressing
      const tempUrl = URL.createObjectURL(file);
      newPics.push({
        url: tempUrl,
        file,
        imageName: `IMAGE${startIdx + idx + 1}_${Math.floor(Math.random() * 100000)}`,
        base64: null,
        isCompressing: true,
        size: file.size,
        sizeLabel: (file.size >= 1024 * 1024) ? (file.size / (1024 * 1024)).toFixed(2) + ' MB' : (file.size / 1024).toFixed(2) + ' KB',
        type: file.type,
        name: file.name
      });
      newCompressingIdxs.push(hotelPictures.length + idx);
    }
    setHotelPictures((prev) => [...prev, ...newPics].slice(0, 3));
    setCompressingHotelImageIdxs((prev) => [...prev, ...newCompressingIdxs].slice(0, 3));

    // Now compress in background using utility
    for (let idx = 0; idx < newPics.length; idx++) {
      const pic = newPics[idx];
      try {
        const { file: compressedFile, base64, name, size, sizeLabel, type } = await import('../../utils/imageCompressor').then(mod => mod.compressAndConvertToBase64(pic.file, { compressionThreshold: 300 * 1024, targetBytes: 300 * 1024 }));
        setHotelPictures((prev) => prev.map((p, i) => {
          if (i === hotelPictures.length + idx) {
            return {
              ...p,
              url: URL.createObjectURL(compressedFile),
              file: compressedFile,
              base64: base64 && base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`,
              isCompressing: false,
              name,
              size,
              sizeLabel,
              type
            };
          }
          return p;
        }));
      } catch (err) {
        showError(err.message || 'Error compressing image.');
        setHotelPictures((prev) => prev.filter((p) => p.url !== pic.url));
      } finally {
        setCompressingHotelImageIdxs((prev) => prev.filter((i) => i !== (hotelPictures.length + idx)));
      }
    }
    e.target.value = '';
    // Do NOT upload images here; upload will happen on Save
  };

  // Remove hotel image from backend when deleted
  // Track images to remove on save
  const [hotelImagesToRemove, setHotelImagesToRemove] = useState([]);
  const handleRemoveHotelPicture = (idx) => {
    const picToRemove = hotelPictures[idx];
    // Remove from state immediately
    setHotelPictures((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      return updated;
    });
    setIsSaveEnabled(true);
    setIsFormChanged(true);
    // If image is already uploaded (no .file property), queue for removal
    if (picToRemove && (picToRemove.url || picToRemove.base64) && acctId && selectedCategory && !picToRemove.file) {
      setHotelImagesToRemove((prev) => [...prev, picToRemove]);
    }
    // If image is not uploaded yet (has .file), just remove from state
    // After removal, hotelPictures state is always up-to-date for saving
  };

  const [categoryQuery, setCategoryQuery] = React.useState('');
  const { showSuccess, showError, NotificationComponent } = useNotifications();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnectionStatusReady, setIsConnectionStatusReady] = useState(false); // Track if connection status is determined

  const [acctIdLoading, setAcctIdLoading] = useState(true);
  const [acctName, setAcctName] = useState('');
  const lastFetchedCategoryRef = useRef(null);
  const isFetchingCategoryRef = useRef(false);

  const [shortDescription, setShortDescription] = useState('');

  const [googlePictureBase64, setGooglePictureBase64] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [newProfileImage, setNewProfileImage] = useState(null);
  const [newProfileImagePreview, setNewProfileImagePreview] = useState(null);
  const [isProfileImageLoading, setIsProfileImageLoading] = useState(true); // Initialize as true
  const fileInputRef = useRef(null);

  const [isCategoryLoading, setIsCategoryLoading] = useState(true); // Add this line
  const [pauseBooking, setPauseBooking] = useState(false); // State for the toggle button
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [initialFormState, setInitialFormState] = useState(null); // Store initial form state for comparison
  const [isFormChanged, setIsFormChanged] = useState(false); // Track if the form has been changed
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false); // State for delete confirmation dialog
  const [accountErrorMsg, setAccountErrorMsg] = useState(''); // New state for account access error message
  const [hasAccountAccessError, setHasAccountAccessError] = useState(false); // Track if there's an account access error
  const [formData, setFormData] = useState({
    availability: {},
    unavailableDates: [],
    appointmentDuration: '',
    bookingTitle: '',
    bookingdescription: '',
    availableDatesOption: 'future',
    numberOfDays: '',
    startDate: null,
    endDate: null,
    appointmentType: 'single', // Default to "Single Appointment"
    remainderFields: [] // Initialize remainderFields array
  });
  const [dropdownOptions, setDropdownOptions] = useState([]); // Will store {id, name} objects
  const [selectedOption, setSelectedOption] = useState(''); // State for selected dropdown value
  const [selectedCategory, setSelectedCategory] = useState(null); // Track the selected category
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [categories, setCategories] = useState([]);
  const [canCreateCategory, setCanCreateCategory] = useState(false); // Track user's category creation permission
  const [userRole, setUserRole] = useState(null); // Track user's role for conditional rendering
  const { setIsPanelLoading, isPanelLoading, acctId, setAcctId } = useOutletContext();
  // State to hold current category data for UI display
  const [categoryState, setCategoryState] = useState(null);

  const token = localStorage.getItem('token');

  // Only fetch categories when acctId is loaded
  useEffect(() => {
    if (acctId && !acctIdLoading) {
      fetchCategoriesFromDatabase();
    }
  }, [acctId, acctIdLoading]);

  // Ensure spinner disappears after loading googlePictureBase64 from backend
  useEffect(() => {
    if (googlePictureBase64) {
      setIsProfileImageLoading(false);
    }
  }, [googlePictureBase64]);

  useEffect(() => {
    // Check URL parameters for OAuth errors
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');

    if (error) {
      if (error === 'access_denied') {
        showError('Google Category authentication was cancelled.');
      } else {
        showError(`Authentication error: ${error}`);
      }

      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // Check if we're returning from OAuth flow
    const authReturnUrl = localStorage.getItem('googleAuthReturnUrl');
    if (authReturnUrl) {
      // Redirect to the saved return URL to restore correct account/category context
      localStorage.removeItem('googleAuthReturnUrl');
      if (window.location.href !== authReturnUrl) {
        window.location.href = authReturnUrl;
      }
    }
  }, [showError]);

  const location = useLocation();
  const accountFromUrl = getAcctNoFromUrl(location.search);

  // Fetch acctId only once when accountNo changes, and set loading state
  useEffect(() => {
    const fetchAcctIdAndInitPanel = async () => {
      setAcctIdLoading(true);
      setIsPanelLoading(true);
      setIsCategoryLoading(true);
      setAccountErrorMsg('');
      setHasAccountAccessError(false);
      // Priority: URL > localStorage > prop
      let accountNo = accountFromUrl || localStorage.getItem('acctNo') || acc;
      if (!accountNo) {
        setAcctId(null);
        setAcctIdLoading(false);
        setHasAccountAccessError(true);
        setAccountErrorMsg('No account number specified in URL');
        setIsPanelLoading(false);
        setIsCategoryLoading(false);
        return;
      }
      // Always update localStorage with the latest accountNo
      localStorage.setItem('acctNo', accountNo);
      try {
        const acctIdFromStorage = localStorage.getItem('acctId') || null;

        if (acctIdFromStorage) {
          setAcctId(acctIdFromStorage);
        } else {
          setAcctId(null);
          setHasAccountAccessError(true);
          setAccountErrorMsg(`You don't have access to account ${accountNo}. Please contact your administrator.`);
          setIsPanelLoading(false);
          setIsCategoryLoading(false);
          return;
        }
      } catch (err) {
        setAcctId(null);
      }
      setAcctIdLoading(false);
    };
    fetchAcctIdAndInitPanel();
    // Utility: Switch account and update URL, localStorage, and trigger re-fetch
    const switchAccount = (newAcctNo) => {
      if (!newAcctNo) return;
      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('acc', newAcctNo);
      window.history.replaceState({}, '', url);
      // Update localStorage
      localStorage.setItem('acctNo', newAcctNo);
      // Trigger re-fetch by updating location.search (React Router)
      window.dispatchEvent(new Event('accountUpdated'));
    };
    // Listen for account changes via custom events (from dashboard)
    const handleAccountVerified = () => {
      fetchAcctIdAndInitPanel();
    };
    window.addEventListener('accountVerified', handleAccountVerified);
    window.addEventListener('accountUpdated', handleAccountVerified);
    return () => {
      window.removeEventListener('accountVerified', handleAccountVerified);
      window.removeEventListener('accountUpdated', handleAccountVerified);
    };
  }, [location.search, acc, accountFromUrl]);





  const fetchCategoriesFromDatabase = async () => {
    setIsCategoryLoading(true);
    setIsProfileImageLoading(true);
    setIsSaveEnabled(false);
    if (!acctId) {
      setIsPanelLoading(false);
      setIsCategoryLoading(false);
      return;
    }
    try {
      // Use acctId from state only
      const userId = localStorage.getItem('userId');
      if (!acctId || !userId) {
        setIsPanelLoading(false);
        setIsCategoryLoading(false);
        return;
      }
      // Fetch categories using acctId
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ui/booking/getCategory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acctId, userId }),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      let categories = data.categories.map((category) => ({
        id: category._id,
        name: category.name
      }));

      setDropdownOptions(categories);
      // If there are no categories, ensure profile image loading is stopped
      if (categories.length === 0) {
        setIsProfileImageLoading(false);
      }

      // --- Category selection persistence logic ---
      // Try to get last selected category for this account from localStorage
      let categoryToFetch = null;
      let localStorageKey = `selectedCategory_${acctId}`;
      let storedCategoryId = localStorage.getItem(localStorageKey);
      if (storedCategoryId && categories.some(cat => cat.id === storedCategoryId)) {
        categoryToFetch = storedCategoryId;
        setSelectedOption(storedCategoryId);
        setSelectedCategory(storedCategoryId);
      } else if (categories.length > 0) {
        categoryToFetch = categories[0].id;
        setSelectedOption(categories[0].id);
        setSelectedCategory(categories[0].id);
      }
      // Only fetch if we have a valid category ID and it's different from the last fetched one
      if (categoryToFetch && categoryToFetch !== lastFetchedCategoryRef.current && acctId) {
        fetchCategoryData(categoryToFetch);
      } else {
        console.warn('[fetchCategoriesFromDatabase] Skipped fetchCategoryData: categoryToFetch or acctId missing', { categoryToFetch, acctId });
        setCategoryState({});
        setIsCategoryLoading(false);
      }
      setCanCreateCategory(true);
    } catch (error) {
      showError('Error fetching category from the database');
    } finally {
      setIsPanelLoading(false);
      setIsCategoryLoading(false);
    }
  }
  useEffect(() => {
    if (!isFormChanged) {
      setIsSaveEnabled(false);
    }
  }, [isFormChanged]);
  const handleConnectClick = () => {
    // Debug: log acctId and account number at click time
    const accountNo = acc || accountFromUrl; // Ensure account number is retrieved correctly
    let effectiveAcctId = acctId;
    const tryApiLookup = async () => {
      // Try to re-lookup acctId from API if not found in state or localStorage
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      if (!userId || !token) return null;
      try {
        const response = await fetch(`/api/ui/booking/verifyAccountAccess/${accountNo}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) return null;
        const data = await response.json();
        if (data.success && data.account && data.account.acctId) {
          return data.account.acctId;
        }
      } catch (e) {
        console.error('[handleConnectClick] Error in API fallback for acctId:', e);
      }
      return null;
    };
    const proceed = async () => {
      // Use acctId from state if available
      if (!effectiveAcctId) {
        // Fallback: Try to get acctId from localStorage accounts
        const accountsJson = localStorage.getItem('accounts');
        if (accountsJson) {
          try {
            const accounts = JSON.parse(accountsJson);
            const currentAccount = accounts.find(acc => acc.acctNo === accountNo || acc.accountNo === accountNo);
            if (currentAccount) {
              effectiveAcctId = currentAccount.acctId;
            }
          } catch (e) {
            console.error('[handleConnectClick] Error parsing accounts for fallback acctId:', e);
          }
        }
      }
      // Final fallback: Try API only if still not found
      if (!effectiveAcctId) {
        effectiveAcctId = await tryApiLookup();
      }
      if (!effectiveAcctId) {
        showError('No account ID found');
        return;
      }
      // ...existing code for redirect...
      const isLocalEnvironment = window.location.hostname === 'localhost';
      const apiBaseUrl = isLocalEnvironment ? 'http://localhost:8083' : (process.env.REACT_APP_API_URL || '');
      // Always use hotel booking OAuth path
      const oauthPath = '/api/booking/oAuth2/redirect';
      const categoryId = selectedOption || 'Category1';
      const defaultSettings = {
        mode: true,
        appointmentType: false,
        sameDayAppointment: false,
        availability: {
          days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
          time: {
            'Mon': [{ start: '09:00', end: '17:00' }],
            'Tue': [{ start: '09:00', end: '17:00' }],
            'Wed': [{ start: '09:00', end: '17:00' }],
            'Thu': [{ start: '09:00', end: '17:00' }],
            'Fri': [{ start: '09:00', end: '17:00' }],
          }
        }
      };
      localStorage.setItem('defaultCategorySettings', JSON.stringify(defaultSettings));
      const redirectUrl = `${apiBaseUrl}${oauthPath}/${effectiveAcctId}?category=${categoryId}`;
      localStorage.setItem('googleAuthReturnUrl', window.location.href);
      // Save the selected category in the URL before redirecting for OAuth
      const url = new URL(window.location.href);
      url.searchParams.set('category', categoryId);
      window.history.replaceState({}, '', url);
      window.location.href = redirectUrl;
    };
    proceed();

    // In development, use the proxy specified in package.json instead of the env variable
    const isLocalEnvironment = window.location.hostname === 'localhost';
    const apiBaseUrl = isLocalEnvironment ? 'http://localhost:8083' : (process.env.REACT_APP_API_URL || '');
    const oauthPath = '/api/booking/oAuth2/redirect';
    const categoryId = selectedOption || 'Category1';



    // Set default settings in localStorage when connecting
    const defaultSettings = {
      mode: true, // Online appointment (Google Meet)
      appointmentType: false, // Single appointment
      sameDayAppointment: false, // Not allowing same day appointments by default
      availability: {
        days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
        time: {
          'Mon': [{ start: '09:00', end: '17:00' }],
          'Tue': [{ start: '09:00', end: '17:00' }],
          'Wed': [{ start: '09:00', end: '17:00' }],
          'Thu': [{ start: '09:00', end: '17:00' }],
          'Fri': [{ start: '09:00', end: '17:00' }],
        }
      }
    }; localStorage.setItem('defaultCalendarSettings', JSON.stringify(defaultSettings));

    // Construct the redirect URL
    const redirectUrl = `${apiBaseUrl}${oauthPath}/${acctId}?category=${categoryId}`;

    // Save the current URL to return to after authentication
    localStorage.setItem('googleAuthReturnUrl', window.location.href);

    // Navigate to the OAuth URL
    window.location.href = redirectUrl;
  };




  const handleToggleChange = async () => {
    setIsPanelLoading(true);
    const newPauseBookingState = !pauseBooking;
    setPauseBooking(newPauseBookingState);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ui/booking/savePauseState', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          acctId,
          categoryId: selectedCategory,
          pauseBooking: newPauseBookingState
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      await response.json();
      if (newPauseBookingState)
        showSuccess('This room category is temporarily paused for booking');
      else
        showSuccess('This room category is now open for booking');
    } catch (error) {
      showError('Error updating pause state');
      setPauseBooking(prevState => !prevState);
    } finally {
      setIsPanelLoading(false);
    }
  }; const handleFieldChange = () => {

    setIsFormChanged(true);
    setIsSaveEnabled(true);
  };

  const handleFormDataChange = (field, value) => {

    // Update form data
    setFormData(prevFormData => {
      const newFormData = {
        ...prevFormData,
        [field]: value
      };

      // Check if the form data is different from the initial state
      if (initialFormState) {
        // Need to handle special case for dates
        const formDataForComparison = {
          ...newFormData,
          startDate: newFormData.startDate ? newFormData.startDate.toISOString() : null,
          endDate: newFormData.endDate ? newFormData.endDate.toISOString() : null,
          unavailableDates: newFormData.unavailableDates.map(date => date ? date.toISOString() : null)
        };

        const currentFormDataStr = JSON.stringify(formDataForComparison);
        const initialFormDataStr = JSON.stringify(initialFormState);

        const hasChanged = currentFormDataStr !== initialFormDataStr;


        setIsFormChanged(hasChanged);
        setIsSaveEnabled(hasChanged);
      } else {
        // If no initial state yet, enable save
        setIsFormChanged(true);
        setIsSaveEnabled(true);
      }

      return newFormData;
    });
  };

  const handleSaveClick = async () => {
    // Remove hotel images queued for deletion
    if (hotelImagesToRemove.length > 0) {
      setIsPanelLoading(true);
      const token = localStorage.getItem('token');
      for (const picToRemove of hotelImagesToRemove) {
        try {
          const response = await fetch('/api/ui/booking/removeHotelImage', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              acctId,
              categoryId: selectedCategory,
              imageUrl: picToRemove.url,
              imageBase64: picToRemove.base64,
              imageName: picToRemove.imageName
            }),
          });
          const result = await response.json();
          if (!response.ok || !result.success) {
            showError(result.message || 'Failed to remove hotel image');
          }
        } catch (error) {
          showError('Error removing hotel image');
        }
      }
      setHotelImagesToRemove([]);
    }
    // Validation: Only required hotel booking fields
    if (!selectedDays.length) {
      showError('Please select at least one day.');
      return;
    }
    if (!numRooms || numRooms < 1) {
      showError('Please enter the number of rooms.');
      return;
    }
    if (!price || price.toString().trim() === '') {
      showError('Price is required.');
      return;
    }
    // Hotel image mandatory validation
    if (!hotelPictures || hotelPictures.length === 0) {
      showError('At least one hotel image is required.');
      return;
    }
    if (!additionalInfo || additionalInfo.trim() === '') {
      showError('Additional Info is required.');
      return;
    }
    if (!checkIn) {
      showError('Please select check-in time.');
      return;
    }
    if (!checkOut) {
      showError('Please select check-out time.');
      return;
    }
    if (!maxPersons || maxPersons < 1) {
      showError('Please enter the maximum persons per room.');
      return;
    }
    if (!availableDatesOption) {
      showError('Please select available dates option.');
      return;
    }
    if (availableDatesOption === 'rolling' && (!rollingDays || isNaN(Number(rollingDays)))) {
      showError('Please enter the number of rolling days.');
      return;
    }
    if (availableDatesOption === 'range' && (!rangeStart || !rangeEnd)) {
      showError('Please select both start and end dates.');
      return;
    }

    setIsPanelLoading(true);

    // Only save images currently in hotelPictures (removed images are excluded)
    let updatedHotelPictures = hotelPictures.filter(pic => pic.url || pic.file);
    let uploadedCount = 0;
    for (let i = 0; i < updatedHotelPictures.length; i++) {
      const pic = updatedHotelPictures[i];
      if (pic.file) {
        const formData = new FormData();
        formData.append('acctId', acctId);
        formData.append('categoryId', selectedCategory);
        // Use original file name for image upload
        const originalName = pic.file.name;
        formData.append('image', pic.file, originalName);
        try {
          const res = await fetch('/api/ui/booking/uploadHotelImage', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          });
          const data = await res.json();
          if (data.success && Array.isArray(data.hotelPictures) && data.hotelPictures.length > 0) {
            // Use the correct image from backend response for each upload
            let matchIdx = data.hotelPictures.findIndex(hp => hp['alt-text'] === originalName || hp['alt-text'] === pic.name);
            if (matchIdx === -1) matchIdx = 0;
            const apiPic = data.hotelPictures[matchIdx];
            updatedHotelPictures[i] = {
              ...pic,
              url: apiPic.src,
              name: apiPic['alt-text'],
              imageName: apiPic['alt-text'],
              file: undefined,
              previewUrl: apiPic.src
            };
            uploadedCount++;
          } else {
            showError(data.message || 'Hotel image upload failed');
            setIsPanelLoading(false);
            return;
          }
        } catch (err) {
          showError('Upload failed: ' + err.message);
          setIsPanelLoading(false);
          return;
        }
      }
    }
    // Ensure each image uses its own previewUrl (AWS url) after upload
    setHotelPictures(updatedHotelPictures.map(pic => ({
      ...pic,
      url: pic.previewUrl || pic.url
    })));
    // Always use the latest hotelPictures state for saving
    updatedHotelPictures = updatedHotelPictures.map(pic => ({
      ...pic,
      url: pic.previewUrl || pic.url
    }));

    // Filter out past unavailable dates before saving
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const filteredUnavailableDates = unavailableDates.filter(date => {
      if (!date) return false;
      const d = date instanceof Date ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())) : new Date(date);
      d.setUTCHours(0, 0, 0, 0);
      return d >= today;
    });
    // Prepare payload with required hotel booking fields in googleCal.settings
    const payload = {
      acctId,
      categoryId: selectedCategory,

      settings: {
        days: selectedDays,
        numRooms,
        price: (() => {
          let num = price.toString().replace(/[^\d.]/g, '');
          let amount = parseFloat(num);
          return isNaN(amount) ? 0 : amount;
        })(),
        extraPersonCharge: (() => {
          let num = extraPersonCharge.toString().replace(/[^\d.]/g, '');
          let amount = parseFloat(num);
          return isNaN(amount) ? 0 : amount;
        })(),
        additionalInfo,
        checkIn,
        checkOut,
        maxPersons,
        availableDatesOption,
        rollingDays: availableDatesOption === 'rolling' ? rollingDays : '',
        rangeStart: availableDatesOption === 'range' ? rangeStart : '',
        rangeEnd: availableDatesOption === 'range' ? rangeEnd : '',
        unavailableDates: filteredUnavailableDates.filter(Boolean).map(date => {
          if (date instanceof Date) {
            // Always save as UTC date string (yyyy-mm-dd)
            const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            return utc.toISOString().split('T')[0];
          }
          return date;
        }),
        hotelPictures: updatedHotelPictures.map((pic) => ({
          url: pic.url,
          name: pic.name || '',
        })),
        shortDescription
      }

    };

    try {
      const token = localStorage.getItem('token');
      // 1. Save main settings
      const response = await fetch('/api/ui/booking/saveSettings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      showSuccess('Hotel booking saved successfully');
      // Optionally reset form or update state here
    } catch (error) {
      showError('Error saving hotel booking');
    } finally {
      setIsPanelLoading(false);
      setIsSaveEnabled(false);
      setIsFormChanged(false);
      // Optionally update initialFormState here
    }
  };

  const handleProfileImageClick = () => {
    fileInputRef.current?.click();
  };
  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      showError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError('Image size should be less than 5MB');
      return;
    }

    // Do not enable Save button for profile image change

    // Show preview immediately from original file
    const previewUrl = URL.createObjectURL(file);
    setNewProfileImagePreview(previewUrl);
    setIsProfileImageLoading(true);

    // Now compress and upload in background
    try {
      const { file: compressedFile } = await import('../../utils/imageCompressor').then(mod => mod.compressAndConvertToBase64(file, {
        targetBytes: 95 * 1024, // Target 95KB to ensure we stay under 100KB
        compressionThreshold: 0,
        allowedOverhead: 1.05, // Strict overhead limit for profile images
        maxWidthOrHeight: 1024, // Smaller dimensions for profile images
        initialQuality: 0.80, // Lower initial quality for profile images
        maxAttempts: 6 // More attempts for better optimization
      }));
      setNewProfileImage(compressedFile);
      // Await upload and only stop loading after upload completes
      await uploadProfileImage(compressedFile);
      setTimeout(() => URL.revokeObjectURL(previewUrl), 2000);
    } catch (err) {
      showError('Failed to compress or upload profile image.');
    }
    // Do not set isProfileImageLoading here; it will be set in uploadProfileImage finally
  };

  // New function to handle immediate image upload
  const uploadProfileImage = async (imageFile, previewData) => {
    if (!imageFile || !selectedCategory) return;

    setIsPanelLoading(true);
    try {
      // Create the FormData to upload
      const formData = new FormData();
      formData.append('image', imageFile, 'profileImage');
      formData.append('acctId', acctId);
      formData.append('categoryId', selectedCategory);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/ui/booking/uploadProfileImage', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Image upload failed: ${response.status}`);
      }

      const imageData = await response.json();
      // If success, update the profile image in the UI using AWS url from backend
      if (imageData.success && imageData.data && imageData.data.url) {
        setGooglePictureBase64(imageData.data.url);
        setProfilePicture(imageData.data.url);
        showSuccess('Profile image updated successfully');
      } else {
        throw new Error('Invalid response from image upload service');
      }
    } catch (error) {
      showError('Failed to upload profile image');
    } finally {
      setIsPanelLoading(false);
      setIsProfileImageLoading(false);
    }
  };


  const openConfirmationDialog = () => {
    setIsConfirmationOpen(true);
  };

  const closeConfirmationDialog = () => {
    setIsConfirmationOpen(false);
  };

  const handleDeleteCategoryClick = () => {
    setIsDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmationDialog = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const confirmDeleteCategory = async () => {
    setIsPanelLoading(true);
    try {
      // First, delete the category

      const response = await fetch('/api/ui/booking/deleteCategory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acctId, categoryId: selectedCategory }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      // Then, make an additional API call to remove category references from all users
      const cleanupResponse = await fetch('/api/ui/booking/removeCategoryFromUsers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categoryId: selectedCategory }),
      });

      if (!cleanupResponse.ok) {
        console.error('Failed to clean up user references:', await cleanupResponse.text());
        // Continue execution even if cleanup fails, but log the error
      }

      setDropdownOptions(prev => prev.filter(cal => cal.id !== selectedCategory));

      if (dropdownOptions.length > 1) {
        const newSelection = dropdownOptions.find(cal => cal.id !== selectedCategory);
        setSelectedOption(newSelection.id);
        setSelectedCategory(newSelection.id);

        // Reset the last fetched category ref to ensure we load fresh data for the newly selected category
        lastFetchedCategoryRef.current = null;
        fetchCategoryData(newSelection.id);
      } else {
        setSelectedOption('');
        setSelectedCategory(null);
        lastFetchedCategoryRef.current = null;
      }

      showSuccess('Category deleted successfully');
    } catch (error) {
      showError('Error deleting category');
    } finally {
      setIsPanelLoading(false);
      closeDeleteConfirmationDialog();
    }
  };
  const fetchCategoryData = async (categoryId) => {
    // Always get acctId from acctNo in URL
    const urlParams = new URLSearchParams(window.location.search);
    const accountNo = urlParams.get('acc') || acc;

    if (!categoryId || !acctId) {
      console.warn('[fetchCategoryData] Missing categoryId or acctId', { categoryId, acctId });
      setIsPanelLoading(false);
      setIsCategoryLoading(false);
      setIsProfileImageLoading(false);
      setIsSaveEnabled(false);
      isFetchingCategoryRef.current = false;
      return;
    }
    // Get acctId from API
    let currentAcctId = null;
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[fetchCategoryData] No token found');
      showError('No token found. Please log in again.');
      setIsPanelLoading(false);
      setIsCategoryLoading(false);
      setIsProfileImageLoading(false);
      setIsSaveEnabled(false);
      isFetchingCategoryRef.current = false;
      return;
    }
    try {
      // Check if we're already fetching this category or if it's the same as the last fetched one
      if (isFetchingCategoryRef.current || (categoryId === lastFetchedCategoryRef.current && categoryId !== null)) {
        setIsPanelLoading(false);
        setIsCategoryLoading(false);
        setIsProfileImageLoading(false);
        setIsSaveEnabled(false);
        return;
      }
      isFetchingCategoryRef.current = true;
      lastFetchedCategoryRef.current = categoryId;

      setIsPanelLoading(true);
      setIsSaveEnabled(false);
      setGooglePictureBase64(null);
      setNewProfileImage(null);
      setNewProfileImagePreview(null);
      setIsProfileImageLoading(true);

      // Now fetch category data with the correct acctId
      const response = await fetch('/api/ui/booking/getCategoryData', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acctId, categoryId }),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        const settings = data.settings || {};
        const category = data.category || {};
        setCategoryState(category);
        // Only overwrite defaults if backend value is not empty/undefined/null
        if (Array.isArray(settings.days) && settings.days.length > 0) setSelectedDays(settings.days);
        if (settings.numRooms !== undefined && settings.numRooms !== null && settings.numRooms !== '') setNumRooms(settings.numRooms);
        // Set checkIn/checkOut and also split into hour/ampm for UI fields
        if (settings.checkIn) {
          setCheckIn(settings.checkIn);
          const { hour, ampm } = splitTime12h(settings.checkIn);
          setCheckInHour(hour);
          setCheckInAMPM(ampm);
        }
        if (settings.checkOut) {
          setCheckOut(settings.checkOut);
          const { hour, ampm } = splitTime12h(settings.checkOut);
          setCheckOutHour(hour);
          setCheckOutAMPM(ampm);
        }
        // Prefer settings.price if available, else fallback to category.metadata
        if (settings.price !== undefined && settings.price !== null && settings.price !== '') setPrice(settings.price);
        else if (category.metadata !== undefined && category.metadata !== null && category.metadata !== '') setPrice(category.metadata);
        if (settings.maxPersons !== undefined && settings.maxPersons !== null && settings.maxPersons !== '') setMaxPersons(settings.maxPersons);
        if (settings.extraPersonCharge !== undefined && settings.extraPersonCharge !== null && settings.extraPersonCharge !== '') setExtraPersonCharge(settings.extraPersonCharge);
        if (settings.availableDatesOption) setAvailableDatesOption(settings.availableDatesOption);
        else if (settings.type) setAvailableDatesOption(settings.type);
        if (settings.rollingDays !== undefined && settings.rollingDays !== null && settings.rollingDays !== '') setRollingDays(settings.rollingDays);
        if (settings.rangeStart) setRangeStart(settings.rangeStart);
        if (settings.rangeEnd) setRangeEnd(settings.rangeEnd);
        if (Array.isArray(settings.unavailableDates) && settings.unavailableDates.length > 0) setUnavailableDates(settings.unavailableDates.map(date => date ? new Date(date) : null));
        else if (settings.unAvailability?.dates && settings.unAvailability.dates.length > 0) setUnavailableDates(settings.unAvailability.dates.map(date => date ? new Date(date) : null));
        if (settings.additionalInfo) setAdditionalInfo(settings.additionalInfo);
        // Sync short description from settings if available, else from category
        if (settings.shortDescription !== undefined && settings.shortDescription !== null && settings.shortDescription !== '') {
          setShortDescription(settings.shortDescription);
        } else {
          setShortDescription(category.description || 'Short description');
        }
        // Set pauseBooking from settings if present, else default to false
        if (typeof settings.pauseBooking === 'boolean') {
          setPauseBooking(settings.pauseBooking);
        } else {
          setPauseBooking(false);
        }

        // Map hotelImages from API response to hotelPictures state
        if (settings.hotelPictures && Array.isArray(settings.hotelPictures) && settings.hotelPictures.length > 0) {
          // Support both {url, name} and {src, alt-text} keys from backend
          const mappedHotelPictures = settings.hotelPictures.map((item, idx) => {
            if (typeof item === 'object' && item !== null) {
              return {
                url: item.url || item.src || '',
                imageName: item.name || item['alt-text'] || `IMAGE${idx + 1}`,
                file: null
              };
            } else {
              return {
                url: '',
                imageName: `IMAGE${idx + 1}`,
                file: null
              };
            }
          });
          setHotelPictures(mappedHotelPictures);
        } else if (settings.hotelImages && Array.isArray(settings.hotelImages)) {
          const mappedHotelPictures = settings.hotelImages.map((img, idx) => ({
            url: img.url,
            base64: img.base64 ? (img.base64.startsWith('data:') ? img.base64 : `data:image/jpeg;base64,${img.base64}`) : undefined,
            imageName: img.name || `IMAGE${idx + 1}`,
            file: null // No local file, just URL from backend
          }));
          setHotelPictures(mappedHotelPictures);
        } else {
          setHotelPictures([]);
        }
        const defaultSettings = JSON.parse(localStorage.getItem('defaultCalendarSettings') || '{}');
        const isNewConnection = !settings.availability?.days?.length;

        setIsConnected(true);
        setIsConnectionStatusReady(true);

        if (settings.googlePictureUrl) {
          setGooglePictureBase64(settings.googlePictureUrl);
          setProfilePicture(settings.profilePicture || settings.googlePictureUrl);
        } else if (settings.profilePicture) {
          setProfilePicture(settings.profilePicture);
          setIsProfileImageLoading(false);
        } else {
          setIsProfileImageLoading(false);
        }
        localStorage.setItem("isConnected", "true");
        const availabilityDays = isNewConnection ? defaultSettings.availability?.days : (settings.availability?.days || []);
        const availabilityTime = isNewConnection && defaultSettings.availability?.time ?
          defaultSettings.availability.time :
          (settings.availability?.time || {});
        const mappedAvailability = Object.keys(availabilityTime).reduce((acc, day) => {
          acc[day] = availabilityTime[day].map(appointment => ({
            start: appointment.start,
            end: appointment.end
          }));
          return acc;
        }, {});
        const unavailableDates = settings.unAvailability?.dates?.map(date =>
          new Date(date)
        ) || [];
        const remainderFields = settings.remainder?.map(rem => {
          let remainderValue, remainderType;
          if (rem.minutes >= 24 * 60) {
            remainderValue = Math.floor(rem.minutes / (24 * 60));
            remainderType = 'days';
          } else if (rem.minutes >= 60) {
            remainderValue = Math.floor(rem.minutes / 60);
            remainderType = 'hours';
          } else {
            remainderValue = rem.minutes;
            remainderType = 'minutes';
          }
          return {
            type: rem.method || '',
            remainderType,
            remainderValue: remainderValue.toString()
          };
        }) || [];
        const newFormData = {
          availability: {
            days: availabilityDays,
            time: mappedAvailability,
            waSlots: settings.availability?.waSlots || {}
          },
          pauseBooking: settings.pauseBooking || false,
          appointmentDuration: settings.slotDurationInMins?.toString() || '60',
          unavailableDates: unavailableDates,
          availableDatesOption: settings.type || 'future',
          numberOfDays: settings.rollingDays?.toString() || '',
          startDate: settings.startDate ? new Date(settings.startDate) : null,
          endDate: settings.endDate ? new Date(settings.endDate) : null,
          mode: isNewConnection ? defaultSettings.mode : (settings.onlineAppointment === true),
          appointmentType: isNewConnection ? defaultSettings.appointmentType : settings.allowMultipleAppointments,
          sameDayAppointment: isNewConnection ? defaultSettings.sameDayAppointment : (settings.sameDayAppointment === true),
          remainderFields: remainderFields
        };
        setFormData(newFormData);
        if (isNewConnection && Object.keys(defaultSettings).length > 0) {
          await saveDefaultSettings(categoryId, defaultSettings);
        }
        // Do not set pauseBooking from category.enabled here to avoid flicker
        setTimeout(() => {
          const formDataCopy = JSON.parse(JSON.stringify({
            ...newFormData,
            startDate: newFormData.startDate ? newFormData.startDate.toISOString() : null,
            endDate: newFormData.endDate ? newFormData.endDate.toISOString() : null,
            unavailableDates: newFormData.unavailableDates.map(date => date ? date.toISOString() : null)
          }));
          setInitialFormState(formDataCopy);
          setIsFormChanged(false);
        }, 100);
      } else {
        setIsConnected(false);
        setIsConnectionStatusReady(true);
        localStorage.removeItem("isConnected");
        setIsProfileImageLoading(false);
      }
    } catch (error) {
      showError('Error loading category data');
      setIsProfileImageLoading(false);
      setIsConnectionStatusReady(true);
    } finally {
      setIsPanelLoading(false);
      setIsCategoryLoading(false);
      setIsSaveEnabled(false);
      isFetchingCategoryRef.current = false;
    }
  };

  // Helper function to save default settings when a new category is connected
  const saveDefaultSettings = async (categoryId, defaultSettings) => {
    try {
      // Prepare the settings object with only required hotel booking fields
      const opt = defaultSettings.availableDatesOption || 'future';
      const settings = {
        days: defaultSettings.days || [],
        numRooms: defaultSettings.numRooms || 1,
        checkIn: defaultSettings.checkIn || '',
        checkOut: defaultSettings.checkOut || '',
        maxPersons: defaultSettings.maxPersons || 1,
        availableDatesOption: opt,
        rollingDays: defaultSettings.rollingDays || '',
        rangeStart: defaultSettings.rangeStart || '',
        rangeEnd: defaultSettings.rangeEnd || '',
        unavailableDates: defaultSettings.unavailableDates || [],
        hotelPictures: defaultSettings.hotelPictures || [],
        price: defaultSettings.price || '',
        additionalInfo: defaultSettings.additionalInfo || '',
        shortDescription: defaultSettings.shortDescription || '',
      };

      // Use acctId from state
      const effectiveAcctId = acctId;
      const userId = localStorage.getItem('userId');
      if (!effectiveAcctId || !userId) {
        showError('Account or user information is missing. Please reload and try again.');
        return;
      }
      const dataToSave = {
        acctId: effectiveAcctId,
        categoryId: categoryId,

        settings: settings
      };
      const token = localStorage.getItem('token');
      await fetch('/api/ui/booking//saveSettings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSave),
      });

      localStorage.removeItem('defaultCategorySettings');
    } catch (error) {
      console.error('Error saving default settings:', error);
      // Don't show an error to the user, as this is a background operation
    }
  };

  const handleDropdownChange = (event) => {
    const selectedValue = event.target.value;
    if (!selectedValue || selectedValue === selectedCategory) {
      return; // Prevent duplicate selection of the same category
    }

    // Persist selected category for this account in localStorage
    if (acctId && selectedValue) {
      localStorage.setItem(`selectedCategory_${acctId}`, selectedValue);
    }

    // Clear current states
    setGooglePictureBase64(null);


    // Show loading indicators immediately to give user feedback
    setIsPanelLoading(true);
    setIsCategoryLoading(true);

    // Reset form tracking states
    setIsSaveEnabled(false);
    setIsFormChanged(false);
    setInitialFormState(null);

    // Update selected category state
    setSelectedOption(selectedValue);
    setSelectedCategory(selectedValue);

    // Update the URL with the selected category for tab isolation and deep linking, but only if different
    const url = new URL(window.location.href);
    const currentCategoryInUrl = url.searchParams.get('category');
    if (currentCategoryInUrl !== selectedValue) {
      url.searchParams.set('category', selectedValue);
      window.history.replaceState({}, '', url);
    }

    // Reset the last fetched category ref to ensure we fetch fresh data
    lastFetchedCategoryRef.current = null;
    isFetchingCategoryRef.current = false; // Reset fetch flag

    // Fetch categories data immediately
    fetchCategoryData(selectedValue);
  };

  // Effect to show error notification when account access error occurs
  useEffect(() => {
    if (hasAccountAccessError && accountErrorMsg) {
      showError(accountErrorMsg);

      // DO NOT update URL here - it would change the URL based on localStorage
      // which defeats the purpose of URL-driven account isolation
      // Each tab should maintain its own account number from the URL

      // Clear error after showing it
      setTimeout(() => {
        setAccountErrorMsg('');
        setHasAccountAccessError(false);
      }, 15000); // Keep the error displayed for 15 seconds
    }
  }, [hasAccountAccessError, accountErrorMsg, showError]);

  // Place these functions before the return statement and before any usage

  const handleAddCategory = async () => {
    setIsPanelLoading(true);
    try {
      if (!acctId) {
        showError('Account not found. Cannot load categories.');
        setIsPanelLoading(false);
        return;
      }
      setProfilePicture(null);
      setNewProfileImagePreview(null);
      setIsProfileImageLoading(false);
      setIsAddCategoryModalOpen(true);
    } catch (e) {
      showError('Failed to load categories');
    } finally {
      setIsPanelLoading(false);
    }
  };

  const handleCreateCategory = async (categoryData) => {
    if (!categoryData.name.trim()) {
      showError('Please enter a category name');
      return;
    }
    if (!canCreateCategory) {
      showError('You do not have permission to create a category.');
      return;
    }
    setIsPanelLoading(true);
    // Reset all form fields to default values when creating a new category
    setSelectedDays(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
    setNumRooms(3);
    setPrice('');
    setAdditionalInfo('');
    setCheckIn('2:00 PM');
    setCheckOut('11:00 AM');
    setMaxPersons(4);
    setAvailableDatesOption('future');
    setRollingDays('');
    setRangeStart('');
    setRangeEnd('');
    setUnavailableDates([]);
    setHotelPictures([]);
    setGooglePictureBase64(null);
    setNewProfileImage(null);
    setNewProfileImagePreview(null);
    setShortDescription('');
    setIsSaveEnabled(false);
    setIsFormChanged(false);
    setPauseBooking(false); // Ensure pause is false for new category
    try {
      const userId = localStorage.getItem('userId');
      if (!acctId) {
        showError('Account not found. Cannot create category.');
        setIsPanelLoading(false);
        return;
      }
      const response = await fetch('/api/ui/booking/addCategory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          acctId,
          userId,
          categoryName: categoryData.name.trim(),

        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! Status: ${response.status}`);
      }
      const newCategory = {
        id: data._id,
        name: categoryData.name.trim()
      };
      // ...existing code for updating admin users, UI, and localStorage...
      setGooglePictureBase64(null);

      setIsConnected(false);
      setDropdownOptions(prev => [...prev, newCategory]);
      setSelectedOption(newCategory.id);
      setSelectedCategory(newCategory.id);
      if (acctId && newCategory.id) {
        localStorage.setItem(`selectedCategory_${acctId}`, newCategory.id);
      }
      const url = new URL(window.location.href);
      const currentCategoryInUrl = url.searchParams.get('booking');
      if (currentCategoryInUrl !== newCategory.id) {
        url.searchParams.set('booking', newCategory.id);
        window.history.replaceState({}, '', url);
      }
      lastFetchedCategoryRef.current = null;
      showSuccess(data.message || `Category "${categoryData.name}" added successfully`);
      setIsAddCategoryModalOpen(false);
      // Save default settings and booking account rel for the new category
      const defaultSettings = {
        days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        numRooms: 3,
        price: '',
        extraPersonCharge: '',
        additionalInfo: '',
        checkIn: '2:00 PM',
        checkOut: '11:00 AM',
        maxPersons: 4,
        availableDatesOption: 'future',
        rollingDays: '',
        rangeStart: '',
        rangeEnd: '',
        unavailableDates: [],
        hotelPictures: [],
        shortDescription: '',
        title: newCategory.name
      };
      await saveDefaultSettings(newCategory.id, defaultSettings);
      await fetchCategoryData(newCategory.id);
      // Force pauseBooking to false after loading new category
      setPauseBooking(false);
      const customEvent = new CustomEvent('categoryListUpdated', { detail: { categoryId: newCategory.id, operation: 'add' } });
      window.dispatchEvent(customEvent);
    } catch (error) {
      showError(error.message);
    } finally {
      setIsPanelLoading(false);
    }
  };

  const handleCategoryNameEdit = async (newName) => {
    // Do not enable Save button for category name change
    // Do not enable Save button for pause booking change
    if (!newName.trim() || newName === selectedCategory) {
      setIsEditingName(false);
      return;
    }
    setIsPanelLoading(true);
    try {
      const response = await fetch('/api/ui/booking/updateCategoryName', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          acctId,
          categoryId: selectedCategory,
          newName: newName.trim()
        }),
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      setDropdownOptions(prev => prev.map(cat =>
        cat.id === selectedCategory ? { ...cat, name: newName.trim() } : cat
      ));
      setCategoryState(prev => prev ? { ...prev, title: newName.trim() } : prev);
      showSuccess('Category name updated successfully');
    } catch (error) {
      showError('Error updating category name');
    } finally {
      setIsPanelLoading(false);
      setIsEditingName(false);
    }
  };

  // Debug: log loading state blockers
  // Removed custom loading overlay; parent handles loading UI to prevent double mask.
  const isAnyImageCompressing = hotelPictures && hotelPictures.some(pic => pic.isCompressing);

  // Skeleton placeholder block: show until all required API data is loaded
  // Only show real form after API/database data is loaded and states are set
  const isDataReady = !acctIdLoading && !isCategoryLoading && categoryState !== null;
  console.log('isDataReady:', isDataReady, {
    acctIdLoading,
    isCategoryLoading,
    formData,
    numRooms,
    price,
    maxPersons,
    additionalInfo,
    availableDatesOption,
    hotelPictures
  });
  if (!isDataReady) {
    return (
      <div className="panel border border-gray-200 bg-white shadow-lg rounded-xl p-6 mt-5 w-full mx-auto relative animate-pulse">
        {/* Header skeleton */}
        <div className="flex items-center justify-between mb-6 w-full">
          <div className="flex items-center">
            {/* Profile image skeleton - same size as actual profile image */}
            <div className="relative group cursor-pointer mr-3">
              <div style={{ width: '90px', height: '96px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e5e7eb', borderRadius: '0.5rem' }}>
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="8" r="4" fill="#9ca3af" />
                  <rect x="4" y="16" width="16" height="4" rx="2" fill="#9ca3af" />
                </svg>
              </div>
            </div>
            {/* Heading skeleton */}
            <div className="h-10 w-48 bg-gray-200 rounded" />
          </div>
          <div className="flex items-center space-x-2">
            {/* Calendar combo box skeleton */}
            <div className="w-56 h-8 bg-gray-200 rounded ml-16" />
          </div>
        </div>
        {/* Main form skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12">
          {/* Room Details skeleton */}
          <div className="flex flex-col space-y-4">
            <div className="h-6 w-32 bg-gray-200 rounded mb-2" />
            <div className="h-9 w-full bg-gray-200 rounded" />
            <div className="h-9 w-full bg-gray-200 rounded" />
            <div className="h-9 w-full bg-gray-200 rounded" />
            <div className="h-20 w-full bg-gray-200 rounded" />
          </div>
          {/* Booking Window skeleton */}
          <div className="flex flex-col space-y-4">
            <div className="h-6 w-32 bg-gray-200 rounded mb-2" />
            <div className="flex flex-wrap gap-2 mb-2">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="h-7 w-12 bg-gray-200 rounded-full" />
              ))}
            </div>
            <div className="h-9 w-full bg-gray-200 rounded" />
            <div className="h-9 w-60 bg-gray-200 rounded" />
            <div className="flex flex-row gap-4">
              <div className="h-9 w-36 bg-gray-200 rounded" />
              <div className="h-9 w-36 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
        {/* Hotel Pictures skeleton */}
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200 mt-6">
          <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
          <div className="flex flex-row gap-4 items-start">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="relative w-24 h-24 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
        {/* Save button skeleton */}
        <div className="flex justify-center mt-6">
          <div className="w-32 h-10 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }
  return (
    <>
      <NotificationComponent />

      {!isCategoryLoading && dropdownOptions.length === 0 ? (
        <div className="flex items-center justify-center h-screen">
          {(() => {


            // Enhanced logic: Show + button if canCreateCategory is true OR if we have a valid account
            // This provides a fallback in case canCreateCategory is not set correctly
            const shouldShowAddButton = canCreateCategory || (acctId && !hasAccountAccessError);

            return shouldShowAddButton;
          })() ? (
            <div className="flex flex-col items-center">
              <PlusIcon
                className="text-gray-600 cursor-pointer hover:text-gray-800 w-16 h-16 transition-colors duration-200"
                onClick={handleAddCategory}
                title="Add Category"
              />
              <p className="mt-2 text-gray-600 text-lg">Click to add your first category</p>
            </div>
          ) : (
            <p className="text-gray-600 text-lg">No categories available</p>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-6  w-full">
            <div className="flex items-center">
              <div className="relative w-12 h-12 flex items-center justify-center mr-3">
                {/* Modern gradient background with shadow */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 rounded-lg shadow-lg"></div>
                {/* Icon with better sizing and color */}
                <BuildingOfficeIcon className="relative w-7 h-7 text-white drop-shadow-sm" />
                {/* Premium glow effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-br from-purple-400/40 via-pink-500/40 to-red-500/40 rounded-lg blur opacity-75 -z-10"></div>
              </div>
              <h1 className="text-3xl font-bold text-black">Bookings</h1>
            </div>
            <div className="flex items-center space-x-2 ">

              {/* Settings Icon for Appointment Setup */}

              <CloudArrowUpIcon
                className="w-5 h-5 text-gray-500 hover:text-black cursor-pointer"
                title="Publish Hotel Booking Flow"
                onClick={async () => {
                  setIsPanelLoading(true);

                  try {
                    const response = await fetch(`/api/booking/waflow/publish/${accountFromUrl}/${acctId}`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      }
                    });

                    if (response.status === 200) {
                      showSuccess('Flow published to WhatsApp manager successfully!');
                    } else {
                      showError(`Publishing failed with status ${response.status}. Please try again later.`);
                    }
                  } catch (error) {
                    console.error('Error publishing flow:', error);
                    showError('Network error occurred. Please check your connection and try again.');
                  } finally {
                    setIsPanelLoading(false);
                  }
                }}
              />
              {canCreateCategory && (
                <PlusIcon
                  className="rounded-md bg-black text-white p-[2px] hover:bg-gray-800 shadow focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 h-5 w-5 cursor-pointer stroke-[2]"
                  onClick={handleAddCategory}
                  title="Add Category"
                />
              )}

              {selectedCategory && dropdownOptions.length > 0 && (
                <TrashIcon
                  onClick={handleDeleteCategoryClick}
                  className={`w-5 h-5 ${dropdownOptions.length === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-red-600 hover:text-red-800 cursor-pointer'
                    }`}
                  title="Delete Selected Category"
                />
              )}

              {/* Category Combobox (Dashboard style, preserves dropdown logic) */}
              <div className="w-56 ml-16">
                <Combobox
                  options={dropdownOptions}
                  value={dropdownOptions.find(opt => opt.id === selectedCategory) || null}
                  onChange={cat => {
                    if (cat && cat.id) {
                      handleDropdownChange({ target: { value: cat.id } });
                    }
                  }}
                  displayValue={cat => cat && cat.name ? cat.name : ''}
                  filter={(cat, query) => cat.name.toLowerCase().includes(query.toLowerCase())}
                  placeholder="Select a category"
                  className="w-full "
                  dropdownClassName=" min-w-[240px] max-w-[320px] bg-white"
                >
                  {(option) => (
                    <ComboboxOption key={option.id} value={option}>
                      <span className="px-3 py-0  block w-full text-left ">{option.name}</span>
                    </ComboboxOption>
                  )}
                </Combobox>
              </div>

            </div>
          </div>

          {selectedCategory && dropdownOptions.length > 0 && (
            <>
              {isPanelLoading && (
                <LoadingMask loading={true} />
              )}
              <div className={`panel border border-gray-200 bg-white shadow-lg rounded-xl p-6${isPanelLoading ? ' opacity-50' : ''} w-full mx-auto relative hover:shadow-xl transition-shadow duration-300`}>
                {/* Profile and Connect button section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 items-center gap-x-12 ">
                  <div className="flex flex-col space-y-3">
                    {/* Profile section */}
                    <div className="flex items-center space-x-3">
                      {/* Only show profile image section when connected to Google */}
                      {isConnected ? (
                        <div className="relative group cursor-pointer" onClick={handleProfileImageClick}>
                          {/* Display new image preview immediately on file select, even before compression is done */}
                          <div className="relative">
                            {(() => { console.log('Profile image src:', newProfileImagePreview || profilePicture); return null; })()}
                            {(newProfileImagePreview || profilePicture) ? (
                              <img
                                src={
                                  newProfileImagePreview
                                    ? newProfileImagePreview
                                    : `${profilePicture}${profilePicture.includes('?') ? '&' : '?'}t=${Date.now()}`

                                }
                                style={{ width: '90px', height: '96px', objectFit: 'cover', display: 'block' }}
                                className={`rounded-lg transition-opacity duration-200 ${(isProfileImageLoading && newProfileImagePreview) ? 'opacity-100' : (isProfileImageLoading ? 'opacity-0' : 'opacity-100')}`}
                                loading="eager"
                                onError={e => {
                                  // If not already tried with crossOrigin, try again
                                  if (!e.target.crossOrigin) {
                                    e.target.crossOrigin = "anonymous";
                                    e.target.src = e.target.src;
                                  } else {
                                    // Fallback to initials SVG
                                    const initials = 'U';
                                    const color = '#4285F4';
                                    const svg = `
                                      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>
                                        <circle cx='50' cy='50' r='50' fill='${color}'/>
                                        <text x='50' y='50' font-family='Arial' font-size='40' 
                                          fill='white' text-anchor='middle' dominant-baseline='central'>
                                          ${initials}
                                        </text>
                                      </svg>
                                    `;
                                    e.target.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
                                  }
                                }}
                              />
                            ) : (
                              <div style={{ width: '90px', height: '96px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e5e7eb', borderRadius: '0.5rem' }}>
                                <FaUserCircle style={{ width: '60px', height: '60px', color: '#9ca3af' }} />
                              </div>
                            )}
                            {isProfileImageLoading && (
                              <div className="absolute inset-0 flex items-center justify-center z-20 rounded-lg bg-white bg-opacity-60 backdrop-blur-xs">
                                <span className="relative flex h-8 w-8">
                                  <span className="animate-spin absolute inline-flex h-full w-full rounded-full border-4 border-solid border-gray-300 border-t-black"></span>
                                </span>
                              </div>
                            )}
                          </div>
                          {/* Camera icon overlay on hover */}
                          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ width: '90px', height: '96px' }}>
                            <FaCamera className="text-white w-8 h-8" />
                          </div>
                          {/* Hidden file input */}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleProfileImageChange}
                            className="hidden"
                          />
                        </div>
                      ) : null}

                      <div className="flex flex-col ">
                        {/* Category name edit section */}
                        <div className="flex items-center ">
                          {isEditingName ? (
                            <input
                              type="text"
                              className="border rounded px-2 py-1 text-base h-6 leading-tight"  /* Increased text size */
                              defaultValue={dropdownOptions.find(opt => opt.id === selectedCategory)?.name}
                              onBlur={(e) => handleCategoryNameEdit(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                              autoFocus

                            />
                          ) : (
                            <div className="flex items-center">
                              <span className="text-base font-medium text-gray-900 mr-2"> {/* Increased text size */}
                                {categoryState?.title || dropdownOptions.find(opt => opt.id === selectedCategory)?.name}
                              </span>
                              <FaPencilAlt
                                className="text-gray-400 hover:text-gray-600 cursor-pointer text-xs"
                                onClick={() => setIsEditingName(true)}
                                title="Edit Category Name"
                              />
                              <FaRegCopy
                                className="text-gray-400 hover:text-gray-600 cursor-pointer text-xs ml-2"
                                onClick={() => {
                                  navigator.clipboard.writeText(selectedCategory);
                                  showSuccess('Category ID copied to clipboard');
                                }}
                                title="Copy Category ID"
                              />
                            </div>
                          )}
                        </div>
                        {/* Short Description textarea, max 80 chars */}
                        <div className="relative mt-1 w-72">
                          <textarea
                            value={shortDescription || ''}
                            onChange={e => {
                              const val = e.target.value.slice(0, 80);
                              setShortDescription(val);
                              setIsSaveEnabled(true);
                              setIsFormChanged(true);
                            }}
                            maxLength={80}
                            placeholder="Short description (max 80 chars)"
                            className="border border-gray-300 rounded-md px-2 py-1 text-sm w-full h-14 resize-none focus:outline-none focus:ring-2 focus:ring-black"
                          />
                          <span className="absolute right-2 bottom-2 text-xs text-gray-400 bg-white bg-opacity-80 px-1 rounded">{(shortDescription || '').length}/80</span>
                        </div>

                      </div>
                    </div>
                  </div>

                  {/* Delete and Save buttons, right-aligned, Save directly below Delete */}
                  <div className="flex flex-col items-end gap-2 mb-2">
                    <Button
                      color="red"
                      className="cursor-pointer min-w-[100px] font-semibold"
                      onClick={handleDeleteCategoryClick}
                      disabled={isPanelLoading}
                    >
                      {isPanelLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Button
                      className="cursor-pointer min-w-[100px] bg-black hover:bg-gray-800 text-white mt-1"
                      onClick={handleSaveClick}
                      disabled={!isSaveEnabled || isPanelLoading || isAnyImageCompressing}
                    >
                      {isPanelLoading ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>

                {/* Always show settings panel, no Google connection check */}
                <>
                  {/* Pause Bookings switch */}
                  <div className="flex items-center gap-8 mt-3">
                    {/* Pause Bookings switch */}
                    <div className="flex-1 flex items-center">
                      <SwitchField className="flex items-center ">
                        <Label className="text-sm font-medium ">Pause Bookings</Label>
                        <Switch
                          name="pauseBooking"
                          checked={pauseBooking}
                          value={pauseBooking}
                          onChange={handleToggleChange}
                          className={`cursor-pointer !transition-all !duration-300 ${pauseBooking ? '!bg-red-500 [--switch-ring:white]' : '!bg-gray-200 [--switch-ring:red]'
                            } !border-0 !outline-none !ring-0 !ring-offset-0 hover:!border-0 hover:!ring-0 hover:!outline-none focus:!ring-0 focus:!ring-offset-0 focus:!border-0`}
                        />
                      </SwitchField>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4 max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row gap-2 w-full">
                      {/* Room Details Panel */}
                      <div className="bg-white rounded-xl shadow p-6 border border-gray-200" style={{ flexBasis: '50%', minWidth: 0 }}>
                        <h4 className="font-semibold mb-4 text-lg">Room Details</h4>
                        {/* No of Rooms */}
                        <div className="flex flex-col gap-1 min-h-[36px]">
                          <label htmlFor="num-rooms" className="text-xs font-medium text-gray-700">No of Rooms</label>
                          <input
                            id="num-rooms"
                            type="number"
                            min={1}
                            value={numRooms}
                            onChange={e => {
                              setNumRooms(Number(e.target.value));
                              setIsSaveEnabled(true);
                              setIsFormChanged(true);
                            }}
                            className="border border-gray-300 rounded-md px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-black w-full"
                            disabled={pauseBooking}
                          />
                        </div>
                        {/* Price */}
                        <div className="flex flex-col gap-1 min-h-[36px] mt-3">
                          <label htmlFor="price" className="text-xs font-medium text-gray-700">Price</label>
                          <div className="flex items-center w-full">
                            <div className="flex items-center w-full border border-gray-300 rounded-md h-9 bg-white focus-within:ring-2 focus-within:ring-black overflow-hidden">
                              <input
                                id="price"
                                type="text"
                                value={
                                  price === '' ? '' :
                                    (() => {
                                      let num = price.toString().replace(/[^\d.]/g, '');
                                      if (!num) return '';
                                      let amount = parseFloat(num);
                                      if (isNaN(amount)) return '';
                                      let formatted = '';
                                      if (currency === 'INR') {
                                        formatted = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount).replace(/\.00$/, '');
                                        // Add space between symbol and number if not present
                                        formatted = formatted.replace(/₹\s?([\d,]+)/, '₹ $1');
                                      } else if (currency === 'USD') {
                                        formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(amount).replace(/\.00$/, '');
                                        // Ensure only one $ and a space: "$123" -> "$ 123"
                                        formatted = formatted.replace(/^\$(\d)/, '$ $$1').replace(/^\$\s?\$/, '$ ');
                                        formatted = formatted.replace(/^\$(\d)/, '$ $1');
                                      } else if (currency === 'EUR') {
                                        formatted = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(amount).replace(/,00\s?€$/, ' €');
                                        formatted = formatted.replace(/([\d,.]+)\s?€/, '$1 €');
                                      } else {
                                        formatted = amount;
                                      }
                                      return formatted;
                                    })()
                                }
                                onChange={e => {
                                  // Remove all non-numeric except dot
                                  let val = e.target.value.replace(/[^\d.]/g, '');
                                  setPrice(val);
                                  setIsSaveEnabled(true);
                                  setIsFormChanged(true);
                                }}
                                className="h-9 text-sm focus:outline-none bg-transparent w-full border-none rounded-none"
                                disabled={pauseBooking}
                                placeholder="Enter price"
                                required
                                style={{ borderRight: 'none' }}
                                inputMode="decimal"
                              />
                              <Combobox
                                dropdownClassName=" min-w-[120px] max-w-[180px] bg-white"
                                as="div"
                                options={[{ id: 'INR', name: 'INR' }, { id: 'USD', name: 'USD' }, { id: 'EUR', name: 'EUR' }]}
                                value={[{ id: 'INR', name: 'INR' }, { id: 'USD', name: 'USD' }, { id: 'EUR', name: 'EUR' }].find(opt => opt.id === (currency || 'INR')) || null}
                                displayValue={opt => (opt && opt.name ? opt.name : '')}
                                onChange={opt => {
                                  if (opt && opt.id) {
                                    setCurrency(opt.id);
                                    setIsSaveEnabled(true);
                                    setIsFormChanged(true);
                                  }
                                }}
                                filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
                                disabled={pauseBooking}
                                className="h-9 text-xs min-w-[120px] max-w-[240px] w-full border-none bg-transparent flex items-center justify-center"

                              >
                                {option => (
                                  <ComboboxOption key={option.id} value={option}>
                                    <span className="px-3 py-0 block w-full text-left">{option.name}</span>
                                  </ComboboxOption>
                                )}
                              </Combobox>
                            </div>
                          </div>

                        </div>
                        {/* Extra person charge */}
                        <div className="flex flex-col gap-1 min-h-[36px] mt-3">
                          <label htmlFor="extra-person-charge" className="text-xs font-medium text-gray-700">Extra person charge</label>
                          <div className="flex items-center w-full">
                            <div className="flex items-center w-full border border-gray-300 rounded-md h-9 bg-white focus-within:ring-2 focus-within:ring-black overflow-hidden">
                              <input
                                id="extra-person-charge"
                                type="text"
                                value={
                                  extraPersonCharge === '' ? '' :
                                    (() => {
                                      let num = extraPersonCharge.toString().replace(/[^\d.]/g, '');
                                      if (!num) return '';
                                      let amount = parseFloat(num);
                                      if (isNaN(amount)) return '';
                                      let formatted = '';
                                      if (currency === 'INR') {
                                        formatted = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount).replace(/\.00$/, '');
                                        formatted = formatted.replace(/₹\s?([\d,]+)/, '₹ $1');
                                      } else if (currency === 'USD') {
                                        formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(amount).replace(/\.00$/, '');
                                        formatted = formatted.replace(/^\$(\d)/, '$ $$1').replace(/^\$\s?\$/, '$ ');
                                        formatted = formatted.replace(/^\$(\d)/, '$ $1');
                                      } else if (currency === 'EUR') {
                                        formatted = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(amount).replace(/,00\s?€$/, ' €');
                                        formatted = formatted.replace(/([\d,.]+)\s?€/, '$1 €');
                                      } else {
                                        formatted = amount;
                                      }
                                      return formatted;
                                    })()
                                }
                                onChange={e => {
                                  let val = e.target.value.replace(/[^\d.]/g, '');
                                  setExtraPersonCharge(val);
                                  setIsSaveEnabled(true);
                                  setIsFormChanged(true);
                                }}
                                className="h-9 text-sm focus:outline-none bg-transparent w-full border-none rounded-none"
                                disabled={pauseBooking}
                                placeholder="Enter extra person charge"
                                required
                                style={{ borderRight: 'none' }}
                                inputMode="decimal"
                              />
                              <Combobox
                                as="div"
                                options={[{ id: 'INR', name: 'INR' }, { id: 'USD', name: 'USD' }, { id: 'EUR', name: 'EUR' }]}
                                value={[{ id: 'INR', name: 'INR' }, { id: 'USD', name: 'USD' }, { id: 'EUR', name: 'EUR' }].find(opt => opt.id === (currency || 'INR')) || null}
                                displayValue={opt => (opt && opt.name ? opt.name : '')}
                                onChange={opt => {
                                  if (opt && opt.id) {
                                    setCurrency(opt.id);
                                    setIsSaveEnabled(true);
                                    setIsFormChanged(true);
                                  }
                                }}
                                filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
                                disabled={pauseBooking}
                                className="h-9 text-xs w-full min-w-[120px] max-w-[180px] border-none bg-transparent flex items-center justify-center"
                                dropdownClassName=" min-w-[120px] max-w-[180px] bg-white"
                              >
                                {option => (
                                  <ComboboxOption key={option.id} value={option}>
                                    <span className="px-3 py-0 block w-full text-left">{option.name}</span>
                                  </ComboboxOption>
                                )}
                              </Combobox>
                            </div>
                          </div>
                        </div>
                        {/* Maximum person per room */}
                        <div className="flex flex-col gap-1 min-h-[36px] mt-3">
                          <label htmlFor="max-persons" className="text-xs font-medium text-gray-700">Maximum adults per room</label>
                          <input
                            id="max-persons"
                            type="number"
                            min={1}
                            value={maxPersons}
                            onChange={e => {
                              setMaxPersons(Number(e.target.value));
                              setIsSaveEnabled(true);
                              setIsFormChanged(true);
                            }}
                            className="border border-gray-300 rounded-md px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-black w-full"
                            disabled={pauseBooking}
                          />
                        </div>
                        {/* Additional Info */}
                        <div className="flex flex-col gap-1 min-h-[36px] mt-3">
                          <label htmlFor="additional-info" className="text-xs font-medium text-gray-700">Additional Info</label>
                          <div className="relative w-full">
                            <textarea
                              id="additional-info"
                              value={additionalInfo}
                              onChange={e => {
                                const val = e.target.value.slice(0, 300);
                                setAdditionalInfo(val);
                                setIsSaveEnabled(true);
                                setIsFormChanged(true);
                              }}
                              maxLength={300}
                              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black w-full min-h-[80px] resize-vertical"
                              disabled={pauseBooking}
                              placeholder="Enter any additional information (max 300 chars)"
                              required
                            />
                            <span className="absolute right-2 bottom-2 text-xs text-gray-400 bg-white bg-opacity-80 px-1 rounded">{(additionalInfo || '').length}/300</span>
                          </div>
                        </div>
                      </div>
                      {/* Booking Window Panel */}
                      <div className="bg-white rounded-xl shadow p-6 border border-gray-200" style={{ flexBasis: '50%', minWidth: 0 }}>
                        <h4 className="font-semibold mb-4 text-lg">Booking Window</h4>
                        {/* Days of the week checkboxes styled like GoogleCalendarPannel */}
                        <div className={`flex flex-col gap-1 ${pauseBooking ? 'opacity-60 pointer-events-none' : ''}`} style={{ minHeight: '36px' }}>
                          <label htmlFor="days-checkbox-group" className="text-xs font-medium text-gray-700">Days</label>
                          <div id="days-checkbox-group" className="flex flex-wrap gap-1 items-center" style={{ minHeight: '28px' }}>
                            {daysOfWeek.map((day) => (
                              <label
                                key={day.value}
                                className={`flex items-center gap-1 px-3 py-0 rounded-full border transition-colors duration-200 cursor-pointer select-none
                                    ${selectedDays.includes(day.value)
                                    ? 'bg-black border-black text-white font-medium shadow'
                                    : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-100'}
                                  `}
                                style={{ minWidth: 48, maxWidth: 48, width: 48, justifyContent: 'center', fontSize: '0.82rem', lineHeight: '1.1', height: '28px', paddingLeft: 0, paddingRight: 0, textAlign: 'center' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDays.includes(day.value)}
                                  onChange={() => handleDayChange(day.value)}
                                  className="hidden"
                                  disabled={pauseBooking}
                                />
                                <span className="font-medium" style={{ letterSpacing: '0.01em', fontSize: '0.82rem' }}>{day.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        {/* Check in & Check out on same row using Combobox */}
                        <div className="flex flex-col md:flex-row  min-h-[36px] mt-3">
                          <div className="flex flex-col flex-1">
                            <label className="text-xs font-medium text-gray-700 mb-1">Check in</label>
                            <div className="flex flex-row gap-2 w-full ">
                              <div style={{ minWidth: '70px', maxWidth: '70px', width: '70px' }}>
                                <Combobox
                                  options={hourOptions.map(h => ({ id: h, name: h }))}
                                  value={hourOptions.map(h => ({ id: h, name: h })).find(opt => opt.id === checkInHour) || null}
                                  displayValue={opt => (opt && opt.name ? opt.name : '')}
                                  onChange={opt => {
                                    if (opt && opt.id) setCheckInHour(opt.id);
                                  }}
                                  filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
                                  disabled={pauseBooking}
                                  className="h-9 text-xs w-full min-w-[70px] max-w-[70px]"
                                  dropdownClassName="min-w-[70px] max-w-[70px] bg-white"
                                >
                                  {option => (
                                    <ComboboxOption key={option.id} value={option}>
                                      <span className="px-3 py-0 block w-full text-left">{option.name}</span>
                                    </ComboboxOption>
                                  )}
                                </Combobox>
                              </div>
                              <div className="flex items-center justify-center" style={{ minWidth: 48, maxWidth: 48 }}>
                                <div
                                  className={`flex items-center w-12 h-6 rounded-full border border-gray-300 bg-gray-100 cursor-pointer select-none relative ${pauseBooking ? 'opacity-60 pointer-events-none' : ''}`}
                                  onClick={() => !pauseBooking && setCheckInAMPM(checkInAMPM === 'AM' ? 'PM' : 'AM')}
                                  style={{ minWidth: 48, maxWidth: 48 }}
                                >
                                  {/* Black circle for unselected value */}
                                  <div
                                    className={`absolute top-0.8 left-0.5 w-5 h-5 rounded-full transition-all duration-200 bg-black`}
                                    style={{ transform: checkInAMPM === 'AM' ? 'translateX(24px)' : 'translateX(0)' }}
                                  ></div>
                                  {/* Selected value as text */}
                                  <span className={`absolute top-0.8 ${checkInAMPM === 'AM' ? 'left-1' : 'right-1'} text-black text-xs font-semibold`} style={{ zIndex: 2 }}>
                                    {checkInAMPM}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col flex-1">
                            <label className="text-xs font-medium text-gray-700 mb-1">Check out</label>
                            <div className="flex flex-row gap-2 w-full mr-2">
                              <div style={{ minWidth: '70px', maxWidth: '70px', width: '70px' }}>
                                <Combobox
                                  options={hourOptions.map(h => ({ id: h, name: h }))}
                                  value={hourOptions.map(h => ({ id: h, name: h })).find(opt => opt.id === checkOutHour) || null}
                                  displayValue={opt => (opt && opt.name ? opt.name : '')}
                                  onChange={opt => {
                                    if (opt && opt.id) setCheckOutHour(opt.id);
                                  }}
                                  filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
                                  disabled={pauseBooking}
                                  className="h-9 text-xs w-full min-w-[70px] max-w-[70px]"
                                  dropdownClassName="min-w-[70px] max-w-[70px] bg-white"
                                >
                                  {option => (
                                    <ComboboxOption key={option.id} value={option}>
                                      <span className="px-3 py-0 block w-full text-left">{option.name}</span>
                                    </ComboboxOption>
                                  )}
                                </Combobox>
                              </div>
                              <div className="flex items-center justify-center" style={{ minWidth: 48, maxWidth: 48 }}>
                                <div
                                  className={`flex items-center w-12 h-6 rounded-full border border-gray-300 bg-gray-100 cursor-pointer select-none relative ${pauseBooking ? 'opacity-60 pointer-events-none' : ''}`}
                                  onClick={() => !pauseBooking && setCheckOutAMPM(checkOutAMPM === 'AM' ? 'PM' : 'AM')}
                                  style={{ minWidth: 48, maxWidth: 48 }}
                                >
                                  {/* Black circle for unselected value */}
                                  <div
                                    className={`absolute top-0.8 left-0.5 w-5 h-5 rounded-full transition-all duration-200 bg-black`}
                                    style={{ transform: checkOutAMPM === 'AM' ? 'translateX(24px)' : 'translateX(0)' }}
                                  ></div>
                                  {/* Selected value as text */}
                                  <span className={`absolute top-0.8 ${checkOutAMPM === 'AM' ? 'left-1' : 'right-1'} text-black text-xs font-semibold`} style={{ zIndex: 2 }}>
                                    {checkOutAMPM}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Available Dates - dropdown */}
                        <div className="flex flex-col gap-1 min-h-[36px] mt-3">
                          <label htmlFor="available-dates-option" className="text-xs font-medium text-gray-700">Available Dates</label>
                          <div className="w-[240px] mr-2">
                            <Combobox
                              options={availableDatesOptions}
                              value={availableDatesOptions.find(opt => opt.id === (availableDatesOption || 'future')) || null}
                              displayValue={opt => (opt && opt.name ? opt.name : '')}
                              onChange={opt => {
                                if (opt && opt.id) {
                                  setAvailableDatesOption(opt.id);
                                  setIsSaveEnabled(true);
                                  setIsFormChanged(true);
                                }
                              }}
                              filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
                              disabled={pauseBooking}
                              className="h-9 text-xs w-full min-w-[120px] max-w-[240px]"
                              dropdownClassName=" min-w-[240px] max-w-[240px] bg-white"
                            >
                              {option => (
                                <ComboboxOption key={option.id} value={option}>
                                  <span className="px-3 py-1 block w-full text-left">{option.name}</span>
                                </ComboboxOption>
                              )}
                            </Combobox>
                          </div>
                        </div>
                        {/* Available Dates - Rolling Days */}
                        {availableDatesOption === 'rolling' && (
                          <div className="flex flex-row items-center gap-2 min-h-[36px] mt-3">
                            <label className="text-xs font-medium leading-6 text-gray-900 text-left mr-2">Number of Days <span className="text-red-500">*</span></label>
                            <input
                              type="number"
                              name="numberOfDays"
                              value={rollingDays === '' ? '60' : rollingDays}
                              onChange={e => {
                                setRollingDays(e.target.value);
                                setIsSaveEnabled(true);
                                setIsFormChanged(true);
                              }}
                              className={`appearance-none border border-gray-300 rounded-lg shadow-sm focus:ring focus:ring-black-300 w-20 h-8 text-xs sm:text-sm text-gray-900 mr-2 ${pauseBooking ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}
                              disabled={pauseBooking}
                              placeholder="Days"
                            />
                            <span className={`text-xs ${pauseBooking ? 'text-gray-400 text-sm' : 'text-gray-900 text-sm'}`}>days</span>
                          </div>
                        )}
                        {/* Available Dates - Date Range (start/end as separate fields) */}
                        {availableDatesOption === 'range' && (
                          <div className="flex flex-col gap-1 mt-3">
                            <div className="flex flex-row items-center gap-4">
                              <label className="text-xs font-medium text-gray-700 mb-1 flex-1">Start Date <span className="text-red-500">*</span></label>
                              <label className="text-xs font-medium text-gray-700 mb-1 flex-1">End Date <span className="text-red-500">*</span></label>
                            </div>
                            <div className="flex flex-row gap-4">
                              <div className="flex-1">
                                <DatePicker
                                  selected={rangeStart ? new Date(rangeStart) : null}
                                  onChange={date => {
                                    function formatLocalDate(d) {
                                      if (!d) return '';
                                      const year = d.getFullYear();
                                      const month = String(d.getMonth() + 1).padStart(2, '0');
                                      const day = String(d.getDate()).padStart(2, '0');
                                      return `${year}-${month}-${day}`;
                                    }
                                    setRangeStart(date ? formatLocalDate(date) : '');
                                    setIsSaveEnabled(true);
                                    setIsFormChanged(true);
                                    if (rangeEnd && date && new Date(rangeEnd) < date) setRangeEnd('');
                                  }}
                                  className={`border border-gray-300 text-sm rounded-md px-3 h-9 focus:outline-none focus:ring-2 focus:ring-black w-full ${pauseBooking ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}
                                  dateFormat="yyyy-MM-dd"
                                  minDate={new Date()}
                                  placeholderText="Select a start date"
                                  disabled={pauseBooking}
                                />
                              </div>
                              <div className="flex-1">
                                <DatePicker
                                  selected={rangeEnd ? new Date(rangeEnd) : null}
                                  onChange={date => {
                                    function formatLocalDate(d) {
                                      if (!d) return '';
                                      const year = d.getFullYear();
                                      const month = String(d.getMonth() + 1).padStart(2, '0');
                                      const day = String(d.getDate()).padStart(2, '0');
                                      return `${year}-${month}-${day}`;
                                    }
                                    setRangeEnd(date ? formatLocalDate(date) : '');
                                    setIsSaveEnabled(true);
                                    setIsFormChanged(true);
                                  }}
                                  className={`border border-gray-300 text-sm rounded-md px-3 h-9 focus:outline-none focus:ring-2 focus:ring-black w-full ${pauseBooking ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}
                                  dateFormat="yyyy-MM-dd"
                                  minDate={rangeStart ? new Date(rangeStart) : new Date()}
                                  placeholderText="Select an end date"
                                  disabled={pauseBooking}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Unavailable Dates */}
                        <div className="flex flex-col gap-1 mt-3">
                          <label className="text-xs font-medium text-gray-700">Unavailable Dates</label>
                          <div className="flex flex-wrap items-start gap-3">
                            {unavailableDates.map((date, index) => (
                              <div
                                key={index}
                                className="inline-flex items-center gap-2"
                              >
                                <DatePicker
                                  selected={date}
                                  onChange={(date) => {
                                    const newDates = [...unavailableDates];
                                    newDates[index] = date;
                                    setUnavailableDates(newDates);
                                    setIsSaveEnabled(true);
                                    setIsFormChanged(true);
                                  }}
                                  className={`w-32 sm:w-36 h-9 border border-gray-300 rounded-lg 
                                      focus:ring-black-500 focus:border-black-500 text-sm
                                      ${pauseBooking ? 'bg-gray-200 text-gray-400' : 'bg-white text-gray-900'}`}
                                  minDate={new Date()}
                                  placeholderText="Select date"
                                  dateFormat="yyyy-MM-dd"
                                  disabled={pauseBooking}
                                />
                                <button
                                  onClick={() => {
                                    const newDates = unavailableDates.filter((_, i) => i !== index);
                                    setUnavailableDates(newDates);
                                    setIsSaveEnabled(true);
                                    setIsFormChanged(true);
                                  }}
                                  className="rounded-md bg-white text-red-600 hover:bg-gray-100 
                                      focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                  disabled={pauseBooking}
                                  title="Remove date"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                setUnavailableDates([...unavailableDates, null]);
                                setIsSaveEnabled(true);
                                setIsFormChanged(true);
                              }}
                              className="rounded-md bg-black text-white p-[2px] hover:bg-gray-800 
                                focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 mt-2 disabled:opacity-50"
                              disabled={pauseBooking}
                              title="Add new unavailable date"
                            >
                              <PlusIcon className="h-4 w-4 stroke-[2]" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Hotel Picture Panel */}
                    <div className="bg-white rounded-xl shadow p-6 border border-gray-200 mt-2">
                      <h4 className="font-semibold mb-4 text-lg">Hotel Picture</h4>
                      <div className="flex items-start min-h-[36px]">
                        <label className="text-sm font-medium text-gray-700 min-w-[170px] mr-4 mt-2">Hotel Pictures (max 3)</label>
                        <div className="flex flex-row gap-4 items-start ml-5">
                          <div
                            className={`relative border-2 border-dashed rounded-lg flex flex-col items-center justify-center p-6 mb-3 ml-4 cursor-pointer transition-colors duration-200 w-[260px] ${hotelPictures.length >= 3 ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60' : 'border-gray-400 hover:border-black bg-white'}`}
                            onClick={() => {
                              if (hotelPictures.length < 3) {
                                document.getElementById('hotel-picture-input').click();
                              } else {
                                showError('You can only upload up to 3 hotel images.');
                              }
                            }}
                            style={{ minHeight: 60 }}
                          >
                            {/* Spinner overlay while compressing */}
                            {isCompressingHotelImages && (
                              <div className="absolute inset-0 bg-white bg-opacity-70 flex flex-col items-center justify-center z-20 rounded-lg">
                                <svg className="animate-spin h-8 w-8 text-black mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                </svg>
                              </div>
                            )}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                            <span className="text-xs text-gray-500">Click or drag to upload hotel images</span>
                            <input
                              id="hotel-picture-input"
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={e => {
                                handleHotelPictureChange(e);
                                setIsSaveEnabled(true);
                                setIsFormChanged(true);
                              }}
                              disabled={hotelPictures.length >= 3}
                              className="hidden"
                            />
                          </div>
                          <div className="flex flex-row flex-wrap gap-2 items-center">
                            {hotelPictures.map((pic, idx) => (
                              <div key={idx} className="relative w-24 h-24 aspect-square border rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center group">
                                <img
                                  src={pic.url}
                                  alt={`Hotel ${idx + 1}`}
                                  className="object-cover w-full h-full cursor-pointer"
                                  onClick={() => {
                                    if (pic.url) {
                                      window.open(pic.url, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                />
                                {pic.isCompressing && (
                                  <>
                                    <div className="absolute inset-0 flex items-center justify-center z-20 rounded-lg bg-white bg-opacity-60 backdrop-blur-xs">
                                      <span className="relative flex h-8 w-8">
                                        <span className="animate-spin absolute inline-flex h-full w-full rounded-full border-4 border-solid border-gray-300 border-t-black"></span>
                                      </span>
                                    </div>
                                    <div className="absolute left-0 right-0 bottom-1 flex justify-center z-30">
                                      <span className="text-xs text-black font-semibold animate-pulse bg-white bg-opacity-80 px-2 py-1 rounded">Compressing...</span>
                                    </div>
                                  </>
                                )}
                                <button
                                  type="button"
                                  className="absolute top-1 right-1 bg-black bg-opacity-70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleRemoveHotelPicture(idx);
                                    setIsSaveEnabled(true);
                                    setIsFormChanged(true);
                                  }}
                                  style={{ lineHeight: 1 }}
                                  tabIndex={-1}
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                          {hotelPictures.length >= 10 && (
                            <div className="text-xs text-red-500 mt-1">Maximum 10 pictures allowed.</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-center mt-6">
                      <Button
                        type="button"
                        onClick={handleSaveClick}
                        disabled={!isSaveEnabled || isPanelLoading || isAnyImageCompressing}
                        className="cursor-pointer min-w-[100px] bg-black hover:bg-gray-800 text-white mt-1"
                      >
                        {isPanelLoading ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                </>
              </div>
            </>
          )}
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={closeDeleteConfirmationDialog}
        onConfirm={confirmDeleteCategory}
        title={`Delete Category - ${dropdownOptions.find(cal => cal.id === selectedCategory)?.name}`}
        message={`Are you sure you want to delete category "${dropdownOptions.find(cal => cal.id === selectedCategory)?.name}"? This action cannot be undone.`}
      />


      <AddCategoryDialog
        isOpen={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
        onSubmit={handleCreateCategory}
        categories={categories}
        userRole={userRole}
      />

      {/* Appointment Setup Modal/Dialog */}
      {isAppointmentSetupOpen && (
        <>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
            <div className="w-[80vw] max-w-[80vw] min-w-[700px] h-[80vh] max-h-[80vh] transform overflow-hidden rounded-lg bg-white p-10 text-left align-middle shadow-2xl transition-all relative flex flex-col" style={{ minHeight: '400px', maxWidth: '80vw', height: '80vh', maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="relative pt-0">
                <button
                  type="button"
                  onClick={() => setIsAppointmentSetupOpen(false)}
                  className="absolute top-2 right-2 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                  aria-label="Close"
                  style={{ top: '-14px', right: '-14px' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-auto min-h-[300px]">
                <AppointmentSetupPage />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );

}
export default GoogleCalendarPanel;